// Modello a blocchi dei contenuti di un tool (pagina pubblica, scheda nel portale,
// guida). Persistito come array JSON in ToolContents.Content, con fallback: se il
// contenuto non è JSON di blocchi valido viene trattato come un singolo blocco
// markdown → retrocompatibile con le descrizioni testuali esistenti.
//
// Condiviso da apps/tools e apps/admin tramite l'alias @easytools/tool-content.
// I tipi ammessi per ciascun contenuto (BLOCKS_BY_KIND) devono restare allineati a
// EasyToolsAPI/Services/ToolContents/ToolContentDocument.cs.

export type CalloutVariant = 'info' | 'success' | 'warning' | 'security';

export interface HeroBlock {
  type: 'hero';
  tagline?: string;
  highlights?: string[];
}
export interface RichTextBlock {
  type: 'richText';
  markdown: string;
}
export interface FeatureItem {
  icon?: string;
  title: string;
  text?: string;
}
export interface FeatureGridBlock {
  type: 'featureGrid';
  title?: string;
  items: FeatureItem[];
}
export interface CalloutBlock {
  type: 'callout';
  variant?: CalloutVariant;
  icon?: string;
  title?: string;
  text: string;
}
export interface StepItem {
  title: string;
  text?: string;
}
export interface StepsBlock {
  type: 'steps';
  title?: string;
  items: StepItem[];
}
export interface ChipListBlock {
  type: 'chipList';
  title?: string;
  chips: string[];
}
export interface SpecRow {
  label: string;
  value: string;
}
export interface SpecsBlock {
  type: 'specs';
  title?: string;
  rows: SpecRow[];
}
export interface FaqItem {
  q: string;
  a: string;
}
export interface FaqBlock {
  type: 'faq';
  title?: string;
  items: FaqItem[];
}
export interface MediaBlock {
  type: 'media';
  url: string;
  alt?: string;
  caption?: string;
}

// ── Solo pagina pubblica ──
export interface GalleryImage {
  url: string;
  alt?: string;
  caption?: string;
}
export interface GalleryBlock {
  type: 'gallery';
  title?: string;
  images: GalleryImage[];
}
export interface CtaBlock {
  type: 'cta';
  title: string;
  text?: string;
  /** Testo del pulsante; predefinito "Entra in Semplora". */
  label?: string;
}
export interface RelatedBlock {
  type: 'related';
  title?: string;
}

// ── Solo guida ──
export interface SectionBlock {
  type: 'section';
  title: string;
}
export interface TableBlock {
  type: 'table';
  title?: string;
  head: string[];
  rows: string[][];
}
export interface PageBreakBlock {
  type: 'pageBreak';
}

// ── Blocchi da builder (2026-10-15) ──
export interface HeadingBlock {
  type: 'heading';
  /** Riga piccola sopra il titolo, facoltativa. */
  eyebrow?: string;
  text: string;
  subtitle?: string;
  /** 2 = titolo di sezione, 3 = sottotitolo. */
  level?: 2 | 3;
}
export type ButtonStyle = 'primary' | 'secondary' | 'link';
export interface ButtonBlock {
  type: 'button';
  label: string;
  /** https://…, mailto:… o /percorso del sito. */
  url: string;
  style?: ButtonStyle;
}
export interface MediaTextBlock {
  type: 'mediaText';
  title?: string;
  markdown: string;
  url: string;
  alt?: string;
  imageSide?: 'left' | 'right';
}
export interface QuoteBlock {
  type: 'quote';
  text: string;
  author?: string;
  role?: string;
}
export interface StatItem {
  value: string;
  label: string;
}
export interface StatsBlock {
  type: 'stats';
  title?: string;
  items: StatItem[];
}
export interface CardItem {
  icon?: string;
  image?: string;
  title: string;
  text?: string;
  url?: string;
  linkLabel?: string;
}
export interface CardsBlock {
  type: 'cards';
  title?: string;
  items: CardItem[];
}
export interface VideoBlock {
  type: 'video';
  /** Indirizzo di YouTube o Vimeo. */
  url: string;
  caption?: string;
}
export interface DividerBlock {
  type: 'divider';
  /** line: una riga; space: solo spazio. */
  style?: 'line' | 'space';
  size?: 'small' | 'medium' | 'large';
}
export interface LogoItem {
  url: string;
  alt: string;
  href?: string;
}
export interface LogosBlock {
  type: 'logos';
  title?: string;
  items: LogoItem[];
}

type ToolDetailBlockContent =
  | HeadingBlock
  | ButtonBlock
  | MediaTextBlock
  | QuoteBlock
  | StatsBlock
  | CardsBlock
  | VideoBlock
  | DividerBlock
  | LogosBlock
  | HeroBlock
  | RichTextBlock
  | FeatureGridBlock
  | CalloutBlock
  | StepsBlock
  | ChipListBlock
  | SpecsBlock
  | FaqBlock
  | MediaBlock
  | GalleryBlock
  | CtaBlock
  | RelatedBlock
  | SectionBlock
  | TableBlock
  | PageBreakBlock;

/**
 * Stile del singolo blocco, sempre da palette: niente colori o misure libere, così una
 * pagina resta coerente. Tutti i campi sono facoltativi; assente = come il resto della sezione.
 */
export interface BlockAppearance {
  spaceTop?: BlockSpace;
  spaceBottom?: BlockSpace;
  align?: 'start' | 'center' | 'end';
  accent?: BlockAccent;
  /** Nasconde il blocco su telefono o su computer e tablet. */
  hideOn?: 'phone' | 'desktop';
  /** Ancora per i link interni: la pagina ha #ancora come indirizzo del blocco. */
  anchor?: string;
}
export type BlockSpace = 'tight' | 'wide' | 'xwide';
export type BlockAccent = 'teal' | 'green' | 'violet' | 'amber' | 'rose' | 'navy';

export type ToolDetailBlock = ToolDetailBlockContent & { appearance?: BlockAppearance };

export type ToolDetailBlockType = ToolDetailBlock['type'];

/** I tre contenuti di un tool. */
export type ToolContentKind = 'publicPage' | 'card' | 'guide';

const CARD_BLOCKS: ToolDetailBlockType[] = [
  'hero', 'featureGrid', 'steps', 'callout', 'chipList', 'specs', 'faq', 'richText', 'media'
];

/**
 * Tipi di blocco ammessi per contenuto, nell'ordine in cui l'editor li propone.
 * La guida diventa un PDF: niente pulsanti, video né loghi.
 */
export const BLOCKS_BY_KIND: Record<ToolContentKind, ToolDetailBlockType[]> = {
  card: ['heading', 'richText', 'hero', 'featureGrid', 'cards', 'steps', 'mediaText', 'stats', 'quote', 'callout',
    'chipList', 'specs', 'faq', 'media', 'video', 'button', 'divider'],
  publicPage: ['heading', 'richText', 'hero', 'featureGrid', 'cards', 'steps', 'mediaText', 'stats', 'quote', 'logos',
    'gallery', 'video', 'callout', 'faq', 'button', 'cta', 'related', 'chipList', 'specs', 'media', 'divider'],
  guide: ['section', 'heading', ...CARD_BLOCKS, 'mediaText', 'stats', 'quote', 'table', 'divider', 'pageBreak'],
};

const KNOWN_TYPES = new Set<string>(Object.values(BLOCKS_BY_KIND).flat());

/**
 * Converte il contenuto grezzo in blocchi. Accetta sia un array di blocchi, sia un
 * oggetto { blocks: [...] }; qualsiasi altra cosa (testo semplice, markdown, JSON
 * non riconosciuto) diventa un singolo blocco richText.
 */
export function parseToolDetail(raw?: string | null): ToolDetailBlock[] {
  const text = (raw ?? '').trim();
  if (!text) return [];

  if (text.startsWith('[') || text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text);
      // Documento a sezioni (formato 2, vedi document.ts): i blocchi in ordine di lettura.
      const arr = Array.isArray(parsed) ? parsed
        : Array.isArray(parsed?.sections) ? parsed.sections.flatMap((s: { blocks?: unknown[] }) => s?.blocks ?? [])
          : parsed?.blocks;
      if (Array.isArray(arr) &&
          arr.length > 0 &&
          arr.every(b => b && typeof b === 'object' && KNOWN_TYPES.has(b.type))) {
        return arr as ToolDetailBlock[];
      }
    } catch {
      // non era JSON valido: fallback markdown
    }
  }

  return [{ type: 'richText', markdown: text }];
}

/** Serializza i blocchi per la persistenza. Stringa vuota se non ci sono blocchi. */
export function serializeToolDetail(blocks: ToolDetailBlock[]): string {
  return blocks.length ? JSON.stringify(blocks) : '';
}

/**
 * Parser "stretto" per il JSON incollato: a differenza di parseToolDetail NON
 * degrada a markdown, ma ritorna null se il contenuto non è un set di blocchi
 * valido. Accetta: array di blocchi, oggetto { blocks: [...] } o un singolo blocco.
 * Con `kind` accetta solo i blocchi ammessi in quel contenuto.
 */
export function tryParseBlocks(text: string, kind?: ToolContentKind): ToolDetailBlock[] | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }

  let candidate: unknown;
  if (Array.isArray(parsed)) {
    candidate = parsed;
  } else if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { blocks?: unknown }).blocks)) {
    candidate = (parsed as { blocks: unknown[] }).blocks;
  } else if (parsed && typeof parsed === 'object' && typeof (parsed as { type?: unknown }).type === 'string') {
    candidate = [parsed]; // singolo blocco → lo avvolgo
  } else {
    return null;
  }

  const allowed = kind ? new Set<string>(BLOCKS_BY_KIND[kind]) : KNOWN_TYPES;
  const list = candidate as unknown[];
  if (list.length === 0) return null;
  if (!list.every(b => b !== null && typeof b === 'object' && allowed.has((b as { type?: string }).type ?? ''))) {
    return null;
  }
  return list as ToolDetailBlock[];
}

/** Etichette italiane dei tipi di blocco (per l'editor admin). */
export const BLOCK_LABELS: Record<ToolDetailBlockType, string> = {
  heading: 'Titolo',
  button: 'Pulsante',
  mediaText: 'Testo e immagine',
  quote: 'Citazione',
  stats: 'Numeri chiave',
  cards: 'Schede',
  video: 'Video',
  divider: 'Separatore',
  logos: 'Loghi',
  hero: 'Intestazione',
  richText: 'Testo',
  featureGrid: 'Griglia funzionalità',
  callout: 'Riquadro evidenziato',
  steps: 'Passi numerati',
  chipList: 'Elenco a pillole',
  specs: 'Tabella chiave-valore',
  faq: 'Domande e risposte',
  media: 'Immagine',
  gallery: 'Galleria di schermate',
  cta: 'Invito all’azione',
  related: 'Tool collegati',
  section: 'Sezione',
  table: 'Tabella',
  pageBreak: 'Interruzione di pagina'
};

/** Icone disponibili per feature/callout/step (nomi mappati a SVG nel renderer). */
export const BLOCK_ICON_NAMES = [
  'radar', 'compare', 'clock', 'activity', 'report', 'lock', 'shield',
  'cloud', 'refresh', 'bolt', 'check', 'star', 'bell', 'database'
] as const;

/** Crea un blocco vuoto del tipo indicato (usato dall'editor "aggiungi blocco"). */
export function createEmptyBlock(type: ToolDetailBlockType): ToolDetailBlock {
  switch (type) {
    case 'heading': return { type, eyebrow: '', text: '', subtitle: '', level: 2 };
    case 'button': return { type, label: '', url: '', style: 'primary' };
    case 'mediaText': return { type, title: '', markdown: '', url: '', alt: '', imageSide: 'right' };
    case 'quote': return { type, text: '', author: '', role: '' };
    case 'stats': return { type, title: '', items: [{ value: '', label: '' }, { value: '', label: '' }, { value: '', label: '' }] };
    case 'cards': return { type, title: '', items: [{ icon: 'star', title: '', text: '' }, { icon: 'star', title: '', text: '' }, { icon: 'star', title: '', text: '' }] };
    case 'video': return { type, url: '', caption: '' };
    case 'divider': return { type, style: 'line', size: 'medium' };
    case 'logos': return { type, title: '', items: [{ url: '', alt: '' }] };
    case 'hero': return { type, tagline: '', highlights: [] };
    case 'richText': return { type, markdown: '' };
    case 'featureGrid': return { type, items: [{ icon: 'star', title: '', text: '' }] };
    case 'callout': return { type, variant: 'info', icon: 'info', title: '', text: '' };
    case 'steps': return { type, items: [{ title: '', text: '' }] };
    case 'chipList': return { type, title: '', chips: [] };
    case 'specs': return { type, rows: [{ label: '', value: '' }] };
    case 'faq': return { type, items: [{ q: '', a: '' }] };
    case 'media': return { type, url: '', alt: '', caption: '' };
    case 'gallery': return { type, title: '', images: [{ url: '', alt: '', caption: '' }] };
    case 'cta': return { type, title: '', text: '', label: '' };
    case 'related': return { type, title: '' };
    case 'section': return { type, title: '' };
    case 'table': return { type, title: '', head: ['', ''], rows: [['', '']] };
    case 'pageBreak': return { type };
  }
}
